import { useTypedDispatch } from "../app/state";
import { useSelector } from "react-redux";
import {
  selectCoordinatesFilter,
  selectCurrentCoverageParams,
  selectCurrentManualFilters,
  selectFilterMapId,
  selectFilterPanelVisibility,
  selectIsCoverageParamsSet,
  selectShowIndoorDeviceList,
  selectSmartFilterDeviceId,
} from "../store/filterStore/filterSelectors";
import { selectDevicesByArrayOfIds } from "../store/deviceStore/deviceSelectors";
import {
  ManualFilterType, setCoordinatesFilter,
  setFiltersMapId,
  setManualFilters,
  setSmartFilterDeviceIdList,
} from "../store/filterStore/filterState";
import DeviceModel from "../models/DeviceModel";
import { convertToHz } from "../helpers/converters/frequency";
import { FilterPanelEnum } from "../store/filterStore/filterPanel.enum";
import { selectCurrentFloor } from "../store/floorStore/floorSelectors";
import { selectCurrentLocation } from "../store/locationStore/locationSelectors";
import {
  Channel,
  filterByChannel, filterByDistance,
  filterByTechnologyConfigurationList,
  filterByWirelessSystemList
} from "./filterFunctions";


interface GeneralFilterSet {
  manualFilters?: ManualFilterType | null,
  smartFilterDeviceId?: number | null,
  mapId: number | null,
  force?: boolean,
}

const useFilter = () => {
  const dispatch = useTypedDispatch();
  const filterIsAppliedForMapId = useSelector(selectFilterMapId);
  const smartFilterDeviceIdList = useSelector(selectSmartFilterDeviceId);
  const smartFilterDeviceList = useSelector(selectDevicesByArrayOfIds(smartFilterDeviceIdList));
  const coordinatesFilter = useSelector(selectCoordinatesFilter);
  const visibleFilterPanel = useSelector(selectFilterPanelVisibility);
  const isManualFiltersPanelVisible = visibleFilterPanel === FilterPanelEnum.FILTERS;
  const isCoveragePanelVisible = visibleFilterPanel === FilterPanelEnum.COVERAGE_OPTIONS;
  const isFilterPanelVisible = isManualFiltersPanelVisible || isCoveragePanelVisible;
  const manualFilters = useSelector(selectCurrentManualFilters);
  const coverageSettings = useSelector(selectCurrentCoverageParams);
  const coverageSettingsAreSet = useSelector(selectIsCoverageParamsSet);
  const currentFloor = useSelector(selectCurrentFloor);
  const currentLocation = useSelector(selectCurrentLocation);
  const currentMap = (currentFloor || currentLocation)?.map;
  const showIndoorDeviceList = useSelector(selectShowIndoorDeviceList);
  const isCoordinatesFilterSet = !!coordinatesFilter.radius;
  const manualFiltersAreChosen = manualFilters &&
    (!!manualFilters.frequencyFrom || !!manualFilters.frequencyTo || manualFilters.technologyConfigurationIdList.length > 0 || manualFilters.wirelessSystemIdList.length > 0)
  const isSmartFilterEnabled = smartFilterDeviceIdList.length > 0;
  const isAnyFilterChosen = isSmartFilterEnabled || manualFiltersAreChosen || isCoordinatesFilterSet;
  const isLocation = currentLocation && !currentFloor;
  const smartFiltersChannelList = smartFilterDeviceList.reduce((sum, deviceFilter) => {
    (deviceFilter?.configuration?.wirelessInterfaces || []).forEach((wirelessInterface) => {
      const bandwidth = wirelessInterface?.technologyConfiguration?.bandwidth || '0 Hz';
      (wirelessInterface?.channels || []).forEach((channel) => {
        sum.push({
          bandwidth: convertToHz(bandwidth),
          centralFrequency: channel.centralFrequencyHz
        });
      })
    })

    return sum;
  }, [] as Channel[]);

  const updateSmartFilterDeviceIdList = (deviceIdList: number[] | null) => {
    dispatch(setSmartFilterDeviceIdList(deviceIdList || []));
  }

  const clearSmartFilter = () => updateSmartFilterDeviceIdList(null);

  const filterDeviceList = (deviceList: DeviceModel[]) => {
    if (!isAnyFilterChosen) {
      return deviceList;
    }

    let filteredDeviceList = deviceList;

    if (manualFiltersAreChosen) {
      const minValue = convertToHz(manualFilters?.frequencyFrom || '0 Hz');
      const maxValue = convertToHz(manualFilters?.frequencyTo || '1000 THz');
      const channel: Channel = {
        centralFrequency: Math.floor((maxValue + minValue) / 2),
        bandwidth: maxValue - minValue,
      };

      filteredDeviceList = filterByChannel(filteredDeviceList, [ channel ]);
    }

    if (isSmartFilterEnabled) {
      filteredDeviceList = filterByChannel(filteredDeviceList, smartFiltersChannelList);
    }

    if (manualFilters?.wirelessSystemIdList && manualFilters.wirelessSystemIdList.length > 0) {
      filteredDeviceList = filterByWirelessSystemList(filteredDeviceList, manualFilters.wirelessSystemIdList);
    }

    if (manualFilters?.technologyConfigurationIdList && manualFilters.technologyConfigurationIdList.length > 0) {
      filteredDeviceList = filterByTechnologyConfigurationList(filteredDeviceList, manualFilters.technologyConfigurationIdList);
    }

    if (isCoordinatesFilterSet) {
      // @ts-ignore
      filteredDeviceList = filterByDistance(filteredDeviceList, coordinatesFilter, currentMap);
    }

    return filteredDeviceList;
  }

  const initializeFiltersForPage = (props: GeneralFilterSet) => {
    const { manualFilters, smartFilterDeviceId, force = false, mapId } = props;

    if (filterIsAppliedForMapId === mapId && isAnyFilterChosen && !force) {
      return;
    }

    if (manualFilters || smartFilterDeviceId) {
      dispatch(setFiltersMapId(mapId));
      dispatch(setManualFilters(manualFilters));
      dispatch(setSmartFilterDeviceIdList([ smartFilterDeviceId ]));
      dispatch(setCoordinatesFilter(null));

      return;
    }

    // no filters initialized for a different mapId
    dispatch(setManualFilters(null));
    dispatch(setSmartFilterDeviceIdList(null));
  }

  return {
    actions: {
      updateSmartFilterDeviceIdList,
      clearSmartFilter,
      filterDeviceList,
      initializeFiltersForPage,
    },
    state: {
      smartFiltersChannelList,
      smartFilterDeviceIdList,
      smartFilterDeviceList,
      isSmartFilterEnabled,
      coverageSettings,
      coordinatesFilter,
      coverageSettingsAreSet,
      isManualFiltersPanelVisible,
      isCoveragePanelVisible,
      isFilterPanelVisible,
      manualFiltersAreChosen,
      manualFilters,
      isCoordinatesFilterSet,
      isAnyFilterChosen,
      isLocation,
      showIndoorDeviceList,
    }
  }
}

export default useFilter;